import React, { useState } from "react";
import api from "@/lib/api";
import { Truck, Package, CheckCircle, Clock, Calendar } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

/**
 * ShipmentsList — renders the per-fabrication shipment timeline inside
 * a Lab & Production fabrication card.
 *
 * Each shipment is a sub-row with its phase number, stage range,
 * dispatch + receipt dates, status pill, and a "Confirm received"
 * button when the doctor hasn't acknowledged delivery yet.
 *
 * Why this lives separately from FabricationCard: phased dispatch can
 * have 2+ shipments per fabrication, so the rendering logic is its own
 * component for clarity. Single-shipment cases render a 1-row stack.
 */
const STATUS_META = {
    pending_dispatch: {
        label: "Preparing dispatch",
        Icon: Clock,
        cls: "bg-slate-100 text-slate-700 border-slate-200",
        dot: "bg-slate-400",
    },
    in_transit: {
        label: "In transit",
        Icon: Truck,
        cls: "bg-amber-50 text-amber-800 border-amber-200",
        dot: "bg-amber-500",
    },
    // iter101.15 — carrier tracking webhook sets this once the courier
    // reports the package as delivered at the clinic. Doctor still has
    // to click Confirm Receipt — this state exists purely to escalate
    // the doctor-facing banner tone and highlight the row in the list.
    delivered: {
        label: "Delivered · confirm receipt",
        Icon: Package,
        cls: "bg-rose-50 text-rose-800 border-rose-300 ring-1 ring-rose-200/60",
        dot: "bg-rose-500 animate-pulse",
    },
    received: {
        label: "Received",
        Icon: CheckCircle,
        cls: "bg-emerald-50 text-emerald-800 border-emerald-200",
        dot: "bg-emerald-500",
    },
};


export default function ShipmentsList({ caseId, shipments, onChange }) {
    const items = shipments || [];
    if (items.length === 0) {
        return (
            <div className="rounded-lg border border-dashed border-border bg-secondary/30 px-3 py-2.5 text-xs text-muted-foreground italic">
                <Package size={14} weight="duotone" className="inline mr-1.5 align-text-bottom" />
                No shipments dispatched yet. Your production team will create one when ready.
            </div>
        );
    }
    return (
        <div className="space-y-2" data-testid={`shipments-list-${caseId}`}>
            {items.map((sh) => (
                <ShipmentRow
                    key={sh.id}
                    caseId={caseId}
                    shipment={sh}
                    onChange={onChange}
                />
            ))}
        </div>
    );
}


function ShipmentRow({ caseId, shipment, onChange }) {
    const meta = STATUS_META[shipment.status] || STATUS_META.pending_dispatch;
    const [confirmOpen, setConfirmOpen] = useState(false);

    return (
        <div
            className="rounded-lg border border-border bg-card overflow-hidden"
            data-testid={`shipment-row-${shipment.id}`}
        >
            <div className="flex items-start gap-3 p-3 flex-wrap sm:flex-nowrap">
                <div className={`w-9 h-9 rounded-full ${meta.cls.split(" ")[0]} flex items-center justify-center shrink-0`}>
                    <meta.Icon size={16} weight="fill" className={meta.cls.split(" ")[1]} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">
                            {shipment.kind === "retainers"
                                ? "Retainers shipment"
                                : `Phase ${shipment.phase_number} · Stages ${shipment.stage_from}–${shipment.stage_to}`}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${meta.cls}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                            {meta.label}
                        </span>
                    </div>
                    <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-muted-foreground">
                        {shipment.dispatched_at && (
                            <span className="inline-flex items-center gap-1">
                                <Truck size={11} weight="duotone" />
                                Dispatched {fmtDate(shipment.dispatched_at)}
                            </span>
                        )}
                        {shipment.received_at && (
                            <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                                <Calendar size={11} weight="duotone" />
                                Received at clinic {fmtDate(shipment.received_at)}
                            </span>
                        )}
                        {shipment.tracking_number && (
                            <span className="inline-flex items-center gap-1 font-mono">
                                {shipment.courier && <span>{shipment.courier}</span>}
                                <span>#{shipment.tracking_number}</span>
                            </span>
                        )}
                    </div>
                    {shipment.received_notes && (
                        <div className="mt-1.5 text-xs text-foreground/80 italic border-l-2 border-emerald-300 pl-2">
                            Receipt note: {shipment.received_notes}
                        </div>
                    )}
                </div>
                {shipment.status !== "received" && (
                    <Button
                        type="button"
                        size="sm"
                        onClick={() => setConfirmOpen(true)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                        data-testid={`shipment-${shipment.id}-confirm-receipt-btn`}
                    >
                        <CheckCircle size={14} className="mr-1.5" weight="fill" />
                        Confirm received
                    </Button>
                )}
            </div>

            <ConfirmReceiptDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                caseId={caseId}
                shipment={shipment}
                onConfirmed={onChange}
            />
        </div>
    );
}


function ConfirmReceiptDialog({ open, onClose, caseId, shipment, onConfirmed }) {
    const [receivedAt, setReceivedAt] = useState(() => todayIsoDate());
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        try {
            setSaving(true);
            // Convert YYYY-MM-DD → ISO datetime at noon local (clinic hours,
            // not midnight UTC which would look weird in IST).
            const iso = receivedAt
                ? new Date(`${receivedAt}T12:00:00`).toISOString()
                : undefined;
            await api.post(
                `/cases/${caseId}/shipments/${shipment.id}/confirm-receipt`,
                { received_at: iso, notes: notes.trim() },
            );
            toast.success("Receipt confirmed.");
            onConfirmed?.();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't confirm receipt. Try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="shipment-confirm-receipt-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <Package size={18} weight="duotone" className="text-emerald-600" />
                        Confirm aligner receipt
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="text-sm text-muted-foreground">
                        Confirm you've received <span className="font-semibold text-foreground">
                            {shipment.kind === "retainers"
                                ? "the retainers"
                                : `Phase ${shipment.phase_number} (stages ${shipment.stage_from}–${shipment.stage_to})`}
                        </span> at your clinic. This timestamps the receipt for the patient's case record.
                    </div>

                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                            Date received
                        </label>
                        <Input
                            type="date"
                            value={receivedAt}
                            onChange={(e) => setReceivedAt(e.target.value)}
                            max={todayIsoDate()}
                            data-testid="shipment-confirm-date-input"
                        />
                        <p className="text-[11px] text-muted-foreground mt-1">
                            Defaults to today. Backdate if the package arrived earlier and you're catching up.
                        </p>
                    </div>

                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                            Notes (optional)
                        </label>
                        <Textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="e.g. Package arrived intact, all stages accounted for."
                            rows={2}
                            maxLength={500}
                            data-testid="shipment-confirm-notes-input"
                        />
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                        <Button variant="ghost" onClick={onClose} data-testid="shipment-confirm-cancel-btn">Cancel</Button>
                        <Button
                            onClick={submit}
                            disabled={saving || !receivedAt}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid="shipment-confirm-submit-btn"
                        >
                            <CheckCircle size={14} className="mr-1.5" weight="fill" />
                            {saving ? "Saving…" : "Confirm receipt"}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}


function todayIsoDate() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch {
        return iso;
    }
}
